/**
 * Tracking API utility functions for the client side
 * Supports Meta Conversion API and other tracking services
 */

/**
 * Hash data using SHA-256 for tracking APIs
 * @param data The data to hash
 * @returns Promise that resolves to SHA-256 hashed string
 */
export const hashData = async (data: string): Promise<string> => {
  // Only hash if data exists and we're in a browser environment
  if (!data || typeof window === 'undefined') return '';
  
  try {
    // Create a SHA-256 hash
    const encoder = new TextEncoder();
    const data_utf8 = encoder.encode(data.trim().toLowerCase());
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', data_utf8);
    
    // Convert the hash to a hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('Hashing error:', error);
    return '';
  }
};

/**
 * Get user data from localStorage and hash it for tracking APIs
 * Using Meta's standard parameter names:
 * - em: Email
 * - ph: Phone
 * - fn: First Name
 * - ln: Last Name
 * - ge: Gender
 * - db: Date of Birth
 * - ct: City
 * - st: State
 * - zp: Zip Code
 * - country: Country
 * 
 * @returns Object with hashed user data using Meta's parameter names
 */
export const getHashedUserData = async (): Promise<Record<string, string>> => {
  if (typeof window === 'undefined') return {};
  
  const userData: Record<string, string> = {};
  
  try {
    const userJson = localStorage.getItem('user');
    if (!userJson) return {};
    
    const user = JSON.parse(userJson);
    
    // Hash user data if available using Meta's standard parameter names
    if (user.email) {
      userData.em = await hashData(user.email);
    }
    
    if (user.phone) {
      userData.ph = await hashData(user.phone);
    }
    
    if (user.first_name) {
      userData.fn = await hashData(user.first_name);
    }
    
    if (user.last_name) {
      userData.ln = await hashData(user.last_name);
    }
    
    if (user.gender) {
      userData.ge = await hashData(user.gender);
    }
    
    if (user.birth_date) {
      userData.db = await hashData(user.birth_date);
    }
    
    return userData;
  } catch (error) {
    console.error('Error getting user data:', error);
    return {};
  }
};

/**
 * Send an AddToCart event to the Meta Conversion API
 * @param productData Product data to send with the event
 * 
 * Note on formatting:
 * - product_id should be in format: "category-color" (e.g., "prescription-glasses-bloody-mary")
 * - product_name should be in proper title case: "Color Category" (e.g., "Bloody Mary Prescription Glasses")
 * - Use slugToString() from functions/slug.ts to convert slugs to properly formatted names
 */
export const sendAddToCartEvent = async (productData: {
  product_id: string;
  product_name: string;
  value: number;
  currency: string;
  content_type?: string;
  content_ids?: string[];
  content_name?: string;
  content_category?: string;
  contents?: Array<{
    id: string;
    quantity: number;
    item_price?: number;
  }>;
}) => {
  // Only send tracking events in production
  if (process.env.NEXT_PUBLIC_ENV !== 'production') {
    return { success: true, message: 'Tracking disabled in non-production environment' };
  }

  try {
    // Get and hash user data from localStorage
    const hashedUserData = await getHashedUserData();
    
    // Combine product data with hashed user data
    const eventData = {
      ...productData,
      ...hashedUserData
    };
    
    const response = await fetch('/api/tracking/add-to-cart', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventData),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      console.error('Error sending tracking event:', result.error || result.message);
    }
    
    return result;
  } catch (error) {
    console.error('Failed to send tracking event:', error);
    return { success: false, message: 'Failed to send event' };
  }
}; 